import { FC, useState, useEffect, useRef } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import BattleLog from '../arena/BattleLog';
import PlayerStats, { PlayerStatsRef } from '../arena/PlayerStats';
import CharacterSelection from '../arena/CharacterSelection';
import WinnerScreen from '../arena/WinnerScreen';
import HeaderNavs from '../headers/nav';
import { characters, Character } from '../../data/characters';
import { Ability, StatusEffect, UltimateState } from '../../data/abilities';
import { WhitelistService } from '../../services/whitelistService';
import Tooltip from '../arena/Tooltip';
import TurnTimer from '../arena/TurnTimer';
import SurrenderButton from '../arena/SurrenderButton';
import MatchHistory from '../arena/MatchHistory';

interface GameState {
  playerHealth: number;
  playerEnergy: number;
  opponentHealth: number;
  opponentEnergy: number;
  isPlayerTurn: boolean;
  gameOver: boolean;
  winner: 'player' | 'opponent' | null;
  playerStatusEffects: StatusEffect[];
  opponentStatusEffects: StatusEffect[];
  playerDefense: number;
  opponentDefense: number;
  consecutiveDodges: number;
  turnTimeLeft: number;
  turns: number;
  playerUltimate: UltimateState;
  opponentUltimate: UltimateState;
  lastUsedAbility: string | null;
}

// Add MatchResult interface
interface MatchResult {
  id: string;
  date: Date;
  playerCharacter: Character;
  opponentCharacter: Character;
  result: 'win' | 'loss';
  turns: number;
}

const MAX_ENERGY = 100;

// Update damage calculation interface
interface DamageCalculationParams {
  defense: number;
  statusEffects: StatusEffect[];
  chainBonus?: number;
}

const GameInfo: FC<{ onClose: () => void }> = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1A2635] rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-blue-400">Rumble Arena: Demo Version</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            ✕
          </button>
        </div>

        <div className="space-y-6">
          <div className="bg-blue-900/30 p-4 rounded-lg border border-blue-500/50">
            <h3 className="text-xl font-semibold text-blue-400 mb-2">🎮 Demo Version</h3>
            <p className="text-gray-300 leading-relaxed">
              Welcome to the demo version of Rumble Arena! This is a preview of our upcoming full game that will feature exclusive rewards, NFT integration, and substantial prizes for top players. In this demo, you can:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-300">
              <li>Test the core combat mechanics</li>
              <li>Try different characters and abilities</li>
              <li>Experience the whitelist challenge system</li>
              <li>Get a taste of what's coming in the full release</li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-yellow-400 mb-3">The Legend</h3>
            <p className="text-gray-300 leading-relaxed">
              In the mystical realm of Etheria, an ancient tradition known as the Rumble Arena has been the ultimate test of valor for generations. Warriors from across different elemental domains - Fire, Water, Earth, and Air - gather to prove their worth and earn their place among the legendary Whitelisted Champions.
            </p>
            <p className="text-gray-300 leading-relaxed mt-2">
              Each champion brings unique abilities forged from their elemental mastery. The Fire Warriors channel destructive flames, Water Mages bend healing currents, Earth Defenders stand unbreakable, and Air Strikers move with unmatched agility. These warriors now face each other in epic battles, seeking to join the ranks of the most elite fighters in the realm.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-green-400 mb-3">The Whitelist Challenge</h3>
            <div className="space-y-3 text-gray-300">
              <p>🏆 <span className="font-semibold">Demo Goal:</span> Win 6 battles to earn your place on the Whitelist and secure early access to the full game's exclusive features</p>
              <p>⚔️ <span className="font-semibold">Battle Rules:</span></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Each battle is turn-based with strategic ability usage</li>
                <li>Manage your energy wisely - abilities cost energy to use</li>
                <li>No health regeneration - battles continue until victory or defeat</li>
                <li>Chain powerful combos for increased damage</li>
                <li>Ultimate abilities can turn the tide of battle</li>
              </ul>
              <p>⚡ <span className="font-semibold">Victory Conditions:</span></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Reduce your opponent's health to zero</li>
                <li>Have more remaining health when both fighters run out of energy</li>
                <li>Force your opponent to surrender</li>
              </ul>
            </div>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-purple-400 mb-3">Coming in Full Release</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-[#0D1829] p-4 rounded">
                <h4 className="text-blue-400 font-semibold mb-2">Game Features</h4>
                <ul className="text-gray-300 space-y-1">
                  <li>🏆 Substantial Prize Pool</li>
                  <li>🎁 Exclusive NFT Rewards</li>
                  <li>🌟 Special Character Skins</li>
                  <li>🏰 Additional Battle Arenas</li>
                </ul>
              </div>
              <div className="bg-[#0D1829] p-4 rounded">
                <h4 className="text-yellow-400 font-semibold mb-2">Gameplay Additions</h4>
                <ul className="text-gray-300 space-y-1">
                  <li>🎯 Ranked Matchmaking</li>
                  <li>🏅 Seasonal Tournaments</li>
                  <li>⚔️ Guild Wars</li>
                  <li>🔥 Enhanced Battle Mechanics</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ArenaSection: FC = () => {
  const { connected, publicKey } = useWallet();
  const [selectedCharacter, setSelectedCharacter] = useState<Character | null>(null);
  const [opponentCharacter, setOpponentCharacter] = useState<Character | null>(null);
  const [battleLog, setBattleLog] = useState<string[]>([]);
  const [isRecordingWin, setIsRecordingWin] = useState(false);
  const [playerStats, setPlayerStats] = useState<{ wins: number; isWhitelisted: boolean } | null>(null);
  const [gameState, setGameState] = useState<GameState>({
    playerHealth: 100,
    playerEnergy: MAX_ENERGY,
    opponentHealth: 100,
    opponentEnergy: MAX_ENERGY,
    isPlayerTurn: true,
    gameOver: false,
    winner: null,
    playerStatusEffects: [],
    opponentStatusEffects: [],
    playerDefense: 0,
    opponentDefense: 0,
    consecutiveDodges: 0,
    turnTimeLeft: 0,
    turns: 0,
    playerUltimate: {
      currentCharge: 0,
      maxCharge: selectedCharacter?.abilities.find(a => a.isUltimate)?.chargeRequired || 5,
      isReady: false
    },
    opponentUltimate: {
      currentCharge: 0,
      maxCharge: opponentCharacter?.abilities.find(a => a.isUltimate)?.chargeRequired || 5,
      isReady: false
    },
    lastUsedAbility: null
  });
  const [showMatchHistory, setShowMatchHistory] = useState(false);
  const [matches, setMatches] = useState<MatchResult[]>([]);
  const [showGameInfo, setShowGameInfo] = useState(false);

  // Load player stats when wallet connects
  useEffect(() => {
    const loadPlayerStats = async () => {
      if (publicKey) {
        try {
          const wins = await WhitelistService.getWins(publicKey);
          const isWhitelisted = await WhitelistService.isWhitelisted(publicKey);
          setPlayerStats({ wins, isWhitelisted });
        } catch (error) {
          console.error('Error loading player stats:', error);
        }
      }
    };
    loadPlayerStats();
  }, [publicKey]);

  const handleCharacterSelect = (character: Character) => {
    setSelectedCharacter(character);
    // Randomly select opponent character
    const availableOpponents = characters.filter(c => c.id !== character.id);
    const randomOpponent = availableOpponents[Math.floor(Math.random() * availableOpponents.length)];
    setOpponentCharacter(randomOpponent);
    
    // Reset game state
    setGameState({
      playerHealth: 100,
      playerEnergy: MAX_ENERGY,
      opponentHealth: 100,
      opponentEnergy: MAX_ENERGY,
      isPlayerTurn: true,
      gameOver: false,
      winner: null,
      playerStatusEffects: [],
      opponentStatusEffects: [],
      playerDefense: 0,
      opponentDefense: 0,
      consecutiveDodges: 0,
      turnTimeLeft: 0,
      turns: 0,
      playerUltimate: {
        currentCharge: 0,
        maxCharge: character.abilities.find(a => a.isUltimate)?.chargeRequired || 5,
        isReady: false
      },
      opponentUltimate: {
        currentCharge: 0,
        maxCharge: randomOpponent.abilities.find(a => a.isUltimate)?.chargeRequired || 5,
        isReady: false
      },
      lastUsedAbility: null
    });
    setBattleLog(['Battle started!']);
  };

  const checkGameOver = (state: GameState) => {
    if (state.playerHealth <= 0) {
      return { gameOver: true, winner: 'opponent' as const };
    }
    if (state.opponentHealth <= 0) {
      return { gameOver: true, winner: 'player' as const };
    }
    return { gameOver: false, winner: null };
  };

  const calculateDamage = (
    ability: Ability,
    attacker: Character,
    defender: Character,
    params: DamageCalculationParams
  ) => {
    // Block any healing effects
    if (ability.type === 'heal' || ability.healing) {
      return { damage: 0, effects: [], isCritical: false, isDodged: false };
    }

    let damage = (ability.damage || 0) * (params.chainBonus || 1);
    
    // Check for critical hit
    const criticalChance = ability.criticalChance || 0;
    const isCritical = Math.random() < criticalChance;
    if (isCritical) {
      damage *= attacker.baseStats.criticalMultiplier;
    }

    // Check for dodge
    const dodgeChance = defender.baseStats.dodgeRate + (ability.dodgeChance || 0);
    const isDodged = Math.random() < dodgeChance;
    if (isDodged) {
      return { damage: 0, effects: [], isCritical, isDodged };
    }

    // Apply defense reduction
    damage = Math.max(0, damage - params.defense);

    // Calculate status effects
    const newEffects: StatusEffect[] = [];
    if (ability.statusEffect && Math.random() < ability.statusEffect.chance) {
      newEffects.push({ ...ability.statusEffect });
    }

    return { damage, effects: newEffects, isCritical, isDodged };
  };

  // Add checkForStalemate function
  const checkForStalemate = (state: GameState): boolean => {
    if (!selectedCharacter || !opponentCharacter) return false;

    // Check if any player has abilities they can use
    const playerCanAct = selectedCharacter.abilities.some(
      ability => ability.energy <= state.playerEnergy && ability.type !== 'heal'
    );
    const opponentCanAct = opponentCharacter.abilities.some(
      ability => ability.energy <= state.opponentEnergy && ability.type !== 'heal'
    );

    return !playerCanAct && !opponentCanAct;
  };

  // Add determineWinnerByHealth function
  const determineWinnerByHealth = (state: GameState): 'player' | 'opponent' => {
    return state.playerHealth > state.opponentHealth ? 'player' : 'opponent';
  };

  // Update handleAbilityUse to check for stalemate
  const handleAbilityUse = (ability: Ability) => {
    if (!gameState.isPlayerTurn || 
        gameState.gameOver || 
        gameState.playerEnergy < ability.energy || 
        ability.type === 'heal' || 
        ability.healing) return;
    if (!selectedCharacter || !opponentCharacter) return;

    // Create new state and log
    const newGameState = { ...gameState };
    const newLog = [...battleLog];

    // Log the turn number
    newLog.push(`Turn ${newGameState.turns + 1}`);
    newGameState.turns++;

    // Apply energy cost first
    const energyCost = ability.energy;
    newGameState.playerEnergy = Math.max(0, newGameState.playerEnergy - energyCost);
    newLog.push(`${selectedCharacter.name} uses ${ability.name} (${energyCost} energy)`);

    // Calculate and apply damage
    const result = calculateDamage(
      ability,
      selectedCharacter,
      opponentCharacter,
      { 
        defense: newGameState.opponentDefense,
        statusEffects: newGameState.opponentStatusEffects
      }
    );

    if (result.isDodged) {
      newLog.push(`${opponentCharacter.name} dodged the attack!`);
    } else {
      // Apply damage
      const damage = result.damage;
      newGameState.opponentHealth = Math.max(0, newGameState.opponentHealth - damage);
      
      const damageMessage = result.isCritical 
        ? `Critical hit! ${selectedCharacter.name} deals ${damage} damage!`
        : `${selectedCharacter.name} deals ${damage} damage!`;
      newLog.push(damageMessage);

      // Show floating damage number
      if (opponentStatsRef.current?.addFloatingNumber) {
        opponentStatsRef.current.addFloatingNumber(
          damage,
          result.isCritical ? 'critical' : 'damage'
        );
      }
    }

    // Apply status effects
    result.effects.forEach(effect => {
      newGameState.opponentStatusEffects.push(effect);
      newLog.push(`${opponentCharacter.name} is afflicted with ${effect.type}!`);
    });

    // Process existing status effects
    processStatusEffects(newGameState, newLog);

    // Log remaining resources
    newLog.push(`${selectedCharacter.name}'s energy: ${newGameState.playerEnergy}/100`);
    newLog.push(`${opponentCharacter.name}'s health: ${newGameState.opponentHealth}/100`);

    // Check for game over
    if (newGameState.opponentHealth <= 0) {
      newGameState.gameOver = true;
      newGameState.winner = 'player';
      newLog.push('Victory! You have won the battle!');
      setGameState(newGameState);
      setBattleLog(newLog);
      if (publicKey) {
        handlePlayerWin(newLog);
      }
      return;
    }

    // Check for stalemate
    if (checkForStalemate(newGameState)) {
      const winner = determineWinnerByHealth(newGameState);
      newGameState.gameOver = true;
      newGameState.winner = winner;
      newLog.push('Neither fighter can continue!');
      newLog.push(`${winner === 'player' ? selectedCharacter.name : opponentCharacter.name} wins with more remaining health!`);
      setGameState(newGameState);
      setBattleLog(newLog);
      if (winner === 'player' && publicKey) {
        handlePlayerWin(newLog);
      }
      return;
    }

    // Switch turns
    newGameState.isPlayerTurn = false;
    
    // Update state
    setGameState(newGameState);
    setBattleLog(newLog);
  };

  const handlePlayAgain = () => {
    setSelectedCharacter(null);
    setOpponentCharacter(null);
    setGameState({
      playerHealth: 100,
      playerEnergy: MAX_ENERGY,
      opponentHealth: 100,
      opponentEnergy: MAX_ENERGY,
      isPlayerTurn: true,
      gameOver: false,
      winner: null,
      playerStatusEffects: [],
      opponentStatusEffects: [],
      playerDefense: 0,
      opponentDefense: 0,
      consecutiveDodges: 0,
      turnTimeLeft: 0,
      turns: 0,
      playerUltimate: {
        currentCharge: 0,
        maxCharge: selectedCharacter?.abilities.find(a => a.isUltimate)?.chargeRequired || 5,
        isReady: false
      },
      opponentUltimate: {
        currentCharge: 0,
        maxCharge: opponentCharacter?.abilities.find(a => a.isUltimate)?.chargeRequired || 5,
        isReady: false
      },
      lastUsedAbility: null
    });
    setBattleLog([]);
  };

  const handlePlayerWin = async (newLog: string[]) => {
    newLog.push('Victory! You won the battle!');
    setIsRecordingWin(true);
    try {
      const qualified = await WhitelistService.addWin(publicKey!);
      const wins = await WhitelistService.getWins(publicKey!);
      const isWhitelisted = await WhitelistService.isWhitelisted(publicKey!);
      setPlayerStats({ wins, isWhitelisted });
      if (qualified) {
        newLog.push('Congratulations! You now qualify for the whitelist!');
      }
    } catch (error) {
      console.error('Error recording win:', error);
      newLog.push('Error recording win. Please try again.');
    } finally {
      setIsRecordingWin(false);
    }
  };

  // Add AI utility functions
  const evaluateAbility = (
    ability: Ability,
    gameState: GameState,
    opponentChar: Character
  ): number => {
    let score = 0;
    
    // Base damage score
    if (ability.damage) {
      score += ability.damage * 1.5;
      // Bonus for critical chance
      if (ability.criticalChance) {
        score += ability.damage * ability.criticalChance * opponentChar.baseStats.criticalMultiplier;
      }
    }

    // Defense value
    if (ability.defense) {
      const healthPercentage = gameState.opponentHealth / 100;
      // Value defense more when health is low
      score += ability.defense * (2 - healthPercentage) * 2;
    }

    // Status effect value
    if (ability.statusEffect) {
      const existingEffect = gameState.playerStatusEffects.find(
        e => e.type === ability.statusEffect?.type
      );
      // Value status effects more if not already applied
      if (!existingEffect) {
        switch (ability.statusEffect.type) {
          case 'stun':
            score += 50 * ability.statusEffect.chance;
            break;
          case 'burn':
            score += 30 * ability.statusEffect.chance;
            break;
          case 'freeze':
            score += 40 * ability.statusEffect.chance;
            break;
        }
      }
    }

    // Chain combo potential
    if (ability.chainBonus && gameState.lastUsedAbility) {
      const canChain = ability.chainBonus.abilities.includes(gameState.lastUsedAbility);
      if (canChain) {
        score *= ability.chainBonus.damageMultiplier || 1.5;
      }
    }

    // Ultimate ability value
    if (ability.isUltimate) {
      if (gameState.opponentUltimate.isReady) {
        // Heavily prioritize ultimate when ready and opponent is vulnerable
        const isOpponentVulnerable = !gameState.playerStatusEffects.some(
          e => e.type === 'freeze' || e.type === 'stun'
        );
        if (isOpponentVulnerable) {
          score *= 2;
        }
      } else {
        // Don't select if not ready
        score = -1000;
      }
    }

    // Energy efficiency
    score /= Math.max(1, ability.energy / 20);

    // Situational adjustments
    const playerHealthPercent = gameState.playerHealth / 100;
    const opponentHealthPercent = gameState.opponentHealth / 100;

    if (opponentHealthPercent < 0.3) {
      // Desperate situation: prefer defensive abilities
      if (ability.defense) score *= 1.5;
    } else if (playerHealthPercent < 0.3) {
      // Finishing blow potential: prefer offensive abilities
      if (ability.damage) score *= 1.5;
    }

    return score;
  };

  // Update handleOpponentTurn to check for stalemate
  const handleOpponentTurn = () => {
    if (!opponentCharacter || !selectedCharacter) return;

    // Get all available abilities that can be used with current energy
    const availableAbilities = opponentCharacter.abilities.filter(
      ability => ability.energy <= gameState.opponentEnergy
    );

    if (availableAbilities.length === 0) {
      // No abilities can be used, end turn
      setGameState(prev => ({ ...prev, isPlayerTurn: true }));
      setBattleLog(prev => [...prev, "Opponent has no usable abilities!"]);
      return;
    }

    // Evaluate each ability and choose the best one
    let bestAbility = availableAbilities[0];
    let bestScore = evaluateAbility(bestAbility, gameState, selectedCharacter);

    availableAbilities.forEach(ability => {
      const score = evaluateAbility(ability, gameState, selectedCharacter);
      if (score > bestScore) {
        bestScore = score;
        bestAbility = ability;
      }
    });

    // Use the selected ability
    const newGameState = { ...gameState };
    const newLog = [...battleLog];

    // Log the turn number
    newLog.push(`Turn ${newGameState.turns + 1}`);
    newGameState.turns++;

    // Apply energy cost
    const energyCost = bestAbility.energy;
    newGameState.opponentEnergy = Math.max(0, newGameState.opponentEnergy - energyCost);
    newLog.push(`${opponentCharacter.name} uses ${bestAbility.name} (${energyCost} energy)`);

    // Calculate and apply damage
    const result = calculateDamage(
      bestAbility,
      opponentCharacter,
      selectedCharacter,
      {
        defense: newGameState.playerDefense,
        statusEffects: newGameState.playerStatusEffects
      }
    );

    if (result.isDodged) {
      newLog.push(`${selectedCharacter.name} dodged the attack!`);
    } else {
      // Apply damage
      const damage = result.damage;
      newGameState.playerHealth = Math.max(0, newGameState.playerHealth - damage);

      const damageMessage = result.isCritical
        ? `Critical hit! ${opponentCharacter.name} deals ${damage} damage!`
        : `${opponentCharacter.name} deals ${damage} damage!`;
      newLog.push(damageMessage);

      // Show floating damage number
      if (playerStatsRef.current?.addFloatingNumber) {
        playerStatsRef.current.addFloatingNumber(
          damage,
          result.isCritical ? 'critical' : 'damage'
        );
      }
    }

    // Apply status effects
    result.effects.forEach(effect => {
      newGameState.playerStatusEffects.push(effect);
      newLog.push(`${selectedCharacter.name} is afflicted with ${effect.type}!`);
    });

    // Process existing status effects
    processStatusEffects(newGameState, newLog);

    // Check for game over
    const gameOverState = checkGameOver(newGameState);
    if (gameOverState.gameOver) {
      newGameState.gameOver = true;
      newGameState.winner = gameOverState.winner;
      if (gameOverState.winner === 'opponent') {
        newLog.push('Game Over! The opponent has won!');
      }
    } else {
      // Switch turn to player
      newGameState.isPlayerTurn = true;
    }

    // Update game state and battle log
    setGameState(newGameState);
    setBattleLog(newLog);
  };

  // Add helper function to process status effects
  const processStatusEffects = (gameState: GameState, battleLog: string[]) => {
    // Process player status effects
    gameState.playerStatusEffects = gameState.playerStatusEffects
      .map(effect => {
        if (effect.damagePerTurn) {
          const dotDamage = effect.damagePerTurn;
          gameState.playerHealth = Math.max(0, gameState.playerHealth - dotDamage);
          battleLog.push(`${effect.type} deals ${dotDamage} damage to ${selectedCharacter?.name}!`);
        }
        return { ...effect, duration: effect.duration - 1 };
      })
      .filter(effect => {
        if (effect.duration <= 0) {
          battleLog.push(`${selectedCharacter?.name} is no longer ${effect.type}ed!`);
          return false;
        }
        return true;
      });

    // Process opponent status effects
    gameState.opponentStatusEffects = gameState.opponentStatusEffects
      .map(effect => {
        if (effect.damagePerTurn) {
          const dotDamage = effect.damagePerTurn;
          gameState.opponentHealth = Math.max(0, gameState.opponentHealth - dotDamage);
          battleLog.push(`${effect.type} deals ${dotDamage} damage to ${opponentCharacter?.name}!`);
        }
        return { ...effect, duration: effect.duration - 1 };
      })
      .filter(effect => {
        if (effect.duration <= 0) {
          battleLog.push(`${opponentCharacter?.name} is no longer ${effect.type}ed!`);
          return false;
        }
        return true;
      });
  };

  // Add refs for PlayerStats components
  const playerStatsRef = useRef<PlayerStatsRef>(null);
  const opponentStatsRef = useRef<PlayerStatsRef>(null);

  const handleTurnTimeout = () => {
    if (gameState.isPlayerTurn) {
      // Auto-select first available ability or skip turn
      const availableAbility = selectedCharacter?.abilities.find(
        ability => ability.energy <= gameState.playerEnergy && ability.type !== 'heal'
      );
      if (availableAbility) {
        handleAbilityUse(availableAbility);
      } else {
        setGameState(prev => ({ ...prev, isPlayerTurn: false }));
        setTimeout(handleOpponentTurn, 1000);
      }
    }
  };

  const handleSurrender = () => {
    const newGameState: GameState = {
      ...gameState,
      gameOver: true,
      winner: 'opponent' as const
    };
    setGameState(newGameState);
    
    // Add to match history
    if (selectedCharacter && opponentCharacter) {
      const newMatch: MatchResult = {
        id: Date.now().toString(),
        date: new Date(),
        playerCharacter: selectedCharacter,
        opponentCharacter: opponentCharacter,
        result: 'loss',
        turns: gameState.turns
      };
      setMatches(prev => [newMatch, ...prev]);
    }
  };

  // Update useEffect to include handleOpponentTurn in dependencies
  useEffect(() => {
    if (!gameState.isPlayerTurn && !gameState.gameOver) {
      const timer = setTimeout(() => {
        handleOpponentTurn();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [gameState.isPlayerTurn, gameState.gameOver, handleOpponentTurn]);

  return (
    <div className="min-h-screen bg-[#0D1829] text-white">
      <HeaderNavs />
      <main className="relative pt-16">
        <div className="container mx-auto px-4 py-8">
          {!connected ? (
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <h1 className="text-4xl font-bold text-blue-400 mb-4">Welcome to Rumble Arena</h1>
                <p className="text-xl text-gray-300">Connect your wallet to begin your journey in the arena!</p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div className="bg-[#1A2635] p-6 rounded-lg border border-blue-500/30">
                  <h2 className="text-2xl font-semibold text-yellow-400 mb-4">🎮 Demo Features</h2>
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-center">
                      <span className="mr-2">⚔️</span>
                      <span>Experience epic turn-based combat</span>
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">🔥</span>
                      <span>Master unique character abilities</span>
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">⚡</span>
                      <span>Chain powerful combo attacks</span>
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">🏆</span>
                      <span>Win battles to earn whitelist spots</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-[#1A2635] p-6 rounded-lg border border-purple-500/30">
                  <h2 className="text-2xl font-semibold text-purple-400 mb-4">🌟 Full Game Preview</h2>
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-center">
                      <span className="mr-2">💰</span>
                      <span>Compete for substantial prize pools</span>
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">🎁</span>
                      <span>Earn exclusive NFT rewards</span>
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">🏰</span>
                      <span>Explore multiple battle arenas</span>
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">⚔️</span>
                      <span>Join epic guild wars</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-[#1A2635] p-8 rounded-lg border border-green-500/30 text-center">
                <h2 className="text-2xl font-semibold text-green-400 mb-4">Ready to Fight?</h2>
                <p className="text-gray-300 mb-6">Connect your wallet to start your journey in the arena!</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <WalletMultiButton />
                  <button
                    onClick={() => setShowGameInfo(true)}
                    className="px-6 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200 flex items-center"
                  >
                    <span className="mr-2">📖</span>
                    Learn More
                  </button>
                </div>
              </div>

              <div className="mt-8 text-center text-sm text-gray-400">
                <p>This is a demo version. Connect your wallet to experience the game and potentially earn a whitelist spot for the full release.</p>
              </div>
            </div>
          ) : !selectedCharacter ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <WalletMultiButton />
                <button
                  onClick={() => setShowGameInfo(true)}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg"
                >
                  How to Play
                </button>
              </div>
              <CharacterSelection onSelect={handleCharacterSelect} />
            </>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-4">
                    <WalletMultiButton />
                    <button
                      onClick={() => setShowMatchHistory(!showMatchHistory)}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded"
                    >
                      {showMatchHistory ? 'Hide History' : 'Show History'}
                    </button>
                  </div>
                  <SurrenderButton
                    onSurrender={handleSurrender}
                    disabled={gameState.gameOver || !gameState.isPlayerTurn}
                  />
                </div>
                {showMatchHistory ? (
                  <MatchHistory matches={matches} />
                ) : (
                  <>
                    <PlayerStats
                      ref={playerStatsRef}
                      character={selectedCharacter}
                      health={gameState.playerHealth}
                      energy={gameState.playerEnergy}
                      isPlayerTurn={gameState.isPlayerTurn}
                      wins={playerStats?.wins ?? 0}
                      isWhitelisted={playerStats?.isWhitelisted ?? false}
                      defense={gameState.playerDefense}
                      statusEffects={gameState.playerStatusEffects}
                      criticalMultiplier={selectedCharacter.baseStats.criticalMultiplier}
                      dodgeRate={selectedCharacter.baseStats.dodgeRate}
                    />
                    {opponentCharacter && (
                      <PlayerStats
                        ref={opponentStatsRef}
                        character={opponentCharacter}
                        health={gameState.opponentHealth}
                        energy={gameState.opponentEnergy}
                        isPlayerTurn={!gameState.isPlayerTurn}
                        isOpponent
                        defense={gameState.opponentDefense}
                        statusEffects={gameState.opponentStatusEffects}
                        criticalMultiplier={opponentCharacter.baseStats.criticalMultiplier}
                        dodgeRate={opponentCharacter.baseStats.dodgeRate}
                      />
                    )}
                  </>
                )}
              </div>
              <div>
                <div className="mb-4">
                  <TurnTimer
                    isActive={!gameState.gameOver}
                    duration={30}
                    onTimeout={handleTurnTimeout}
                  />
                </div>
                <BattleLog messages={battleLog} />
                <div className="mt-4 grid grid-cols-2 gap-4">
                  {selectedCharacter.abilities.map((ability, index) => {
                    const isDisabled = !gameState.isPlayerTurn ||
                      gameState.gameOver ||
                      gameState.playerEnergy < ability.energy ||
                      ability.type === 'heal' ||
                      (ability.isUltimate && !gameState.playerUltimate.isReady);

                    const canChain = ability.chainBonus?.abilities.includes(gameState.lastUsedAbility || '') || false;
                    
                    return (
                      <Tooltip
                        key={index}
                        content={
                          <div>
                            <div className="font-bold mb-1">{ability.name}</div>
                            <div className="text-sm">{ability.description}</div>
                            {ability.statusEffect && (
                              <div className="text-sm mt-1">
                                Applies {ability.statusEffect.type} for {ability.statusEffect.duration} turns
                              </div>
                            )}
                            {ability.chainBonus && (
                              <div className="text-sm mt-1 text-yellow-400">
                                Chain Bonus: +{((ability.chainBonus.damageMultiplier || 1) - 1) * 100}% damage
                              </div>
                            )}
                            {ability.isUltimate && (
                              <div className="text-sm mt-1 text-purple-400">
                                Ultimate: Requires {ability.chargeRequired} turns to charge
                              </div>
                            )}
                          </div>
                        }
                      >
                        <div className={`relative ${canChain ? 'ring-2 ring-yellow-400 animate-pulse' : ''}`}>
                          <button
                            onClick={() => handleAbilityUse(ability)}
                            disabled={isDisabled}
                            className={`w-full p-3 rounded relative ${
                              isDisabled
                                ? 'bg-gray-600 cursor-not-allowed opacity-75'
                                : ability.isUltimate
                                ? 'bg-purple-600 hover:bg-purple-700'
                                : 'bg-blue-600 hover:bg-blue-700'
                            } transform hover:scale-105 transition-all`}
                          >
                            {ability.type === 'heal' ? (
                              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                                <span className="text-red-500">Healing Disabled</span>
                              </div>
                            ) : null}
                            <div className="flex justify-between items-center mb-2">
                              <span className="font-bold">{ability.name}</span>
                              <span className="text-sm bg-blue-900 px-2 py-1 rounded">
                                {ability.energy} ⚡
                              </span>
                            </div>
                            <div className="text-sm text-gray-300 mb-2">{ability.description}</div>
                            <div className="flex justify-between text-sm">
                              {ability.damage && (
                                <span className="text-red-400">
                                  Damage: {ability.damage}
                                </span>
                              )}
                              {ability.defense && (
                                <span className="text-blue-400">
                                  Defense: {ability.defense}
                                </span>
                              )}
                              {ability.healing && (
                                <span className="text-green-400">
                                  Heal: {ability.healing}
                                </span>
                              )}
                              {ability.statusEffect && (
                                <span className={`text-${
                                  ability.statusEffect.type === 'burn' ? 'red' :
                                  ability.statusEffect.type === 'freeze' ? 'blue' :
                                  'yellow'
                                }-400`}>
                                  {ability.statusEffect.type} ({Math.round(ability.statusEffect.chance * 100)}%)
                                </span>
                              )}
                            </div>
                            {ability.criticalChance && (
                              <div className="absolute top-1 right-1 text-xs text-yellow-400">
                                Crit: {Math.round(ability.criticalChance * 100)}%
                              </div>
                            )}
                            {ability.isUltimate && (
                              <div className="absolute bottom-0 left-0 w-full h-1 bg-gray-700">
                                <div
                                  className="h-full bg-purple-400 transition-all duration-300"
                                  style={{
                                    width: `${(gameState.playerUltimate.currentCharge / gameState.playerUltimate.maxCharge) * 100}%`
                                  }}
                                />
                              </div>
                            )}
                          </button>
                        </div>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          {gameState.gameOver && selectedCharacter && (
            <WinnerScreen
              winner={gameState.winner as 'player' | 'opponent'}
              character={selectedCharacter}
              onPlayAgain={handlePlayAgain}
              isRecordingWin={isRecordingWin}
              playerStats={playerStats}
            />
          )}
          {showGameInfo && <GameInfo onClose={() => setShowGameInfo(false)} />}
        </div>
      </main>
    </div>
  );
};

export default ArenaSection;
